import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";

import "swiper/css";
import "swiper/css/free-mode";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { replaceCategories, clearCategories } from "../../productSlice";
import {
  selectCategoryTree,
  HierarchicalCategory,
} from "../../productSelectors";
import { useGetCategoriesQuery } from "../../api/productTaxonomyApi";

const MobileCategoryStrip: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  const { isLoading: isCategoriesLoading } = useGetCategoriesQuery();
  const categoryTree = useAppSelector(selectCategoryTree);
  const selectedCategories = useAppSelector(
    (state) => state.product.filters.category,
  );

  const isComponentLoading = isCategoriesLoading || !areTranslationsReady;

  // --- LOGIC: Find Active Path (Level 1, 2, and 3) ---
  const { activeL1, activeL2 } = useMemo(() => {
    if (selectedCategories.length === 0)
      return { activeL1: null, activeL2: null };

    let foundL1: HierarchicalCategory | null = null;
    let foundL2: HierarchicalCategory | null = null;

    // We look for the branch that contains our selection
    for (const l1 of categoryTree) {
      const isL1Selected = selectedCategories.includes(l1.id);
      const hasL2Selected = l1.children.some(
        (child) =>
          selectedCategories.includes(child.id) ||
          child.children.some((gChild) =>
            selectedCategories.includes(gChild.id),
          ),
      );

      if (isL1Selected || hasL2Selected) {
        foundL1 = l1;
        // Now look specifically for the L2 child in this branch
        foundL2 =
          l1.children.find(
            (l2) =>
              selectedCategories.includes(l2.id) ||
              l2.children.some((gChild) =>
                selectedCategories.includes(gChild.id),
              ),
          ) || null;
        break;
      }
    }

    return { activeL1: foundL1, activeL2: foundL2 };
  }, [categoryTree, selectedCategories]);

  const handleCategoryClick = (id: number) => {
    if (selectedCategories.includes(id)) {
      dispatch(clearCategories());
    } else {
      dispatch(replaceCategories([id]));
    }
  };

  if (isComponentLoading) {
    return (
      <div className="mobile-category-strip">
        <div className="mobile-category-strip__skeleton-track">
          <Skeleton width="100%" height={45} borderRadius={0} />
        </div>
      </div>
    );
  }

  if (categoryTree.length === 0) return null;

  return (
    <div className="mobile-category-strip">
      {/* --- ROW 1: LEVEL 1 (Root Parents) --- */}
      <div className="mobile-category-strip__row mobile-category-strip__row--l1">
        <Swiper
          modules={[FreeMode]}
          freeMode
          slidesPerView="auto"
          spaceBetween={0}
        >
          <SwiperSlide className="mobile-category-strip__slide">
            <button
              className={`mobile-category-strip__item ${selectedCategories.length === 0 ? "is-active" : ""}`}
              onClick={() => dispatch(clearCategories())}
            >
              {t("productsArchive.sidebar.allCategories", "All")}
            </button>
          </SwiperSlide>
          {categoryTree.map((cat) => (
            <SwiperSlide key={cat.id} className="mobile-category-strip__slide">
              <button
                className={`mobile-category-strip__item ${activeL1?.id === cat.id ? "is-active" : ""}`}
                onClick={() => handleCategoryClick(cat.id)}
              >
                {cat.name}
              </button>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* --- ROW 2: LEVEL 2 (Children of L1) --- */}
      {activeL1 && activeL1.children.length > 0 && (
        <div className="mobile-category-strip__row mobile-category-strip__row--l2">
          <Swiper
            modules={[FreeMode]}
            freeMode
            slidesPerView="auto"
            spaceBetween={0}
          >
            <SwiperSlide className="mobile-category-strip__slide">
              <button
                className={`mobile-category-strip__item mobile-category-strip__item--sub ${selectedCategories.includes(activeL1.id) ? "is-active" : ""}`}
                onClick={() => handleCategoryClick(activeL1.id)}
              >
                {t("productsArchive.sidebar.allInParent", {
                  defaultValue: "All",
                })}
              </button>
            </SwiperSlide>
            {activeL1.children.map((cat) => (
              <SwiperSlide
                key={cat.id}
                className="mobile-category-strip__slide"
              >
                <button
                  className={`mobile-category-strip__item mobile-category-strip__item--sub ${activeL2?.id === cat.id ? "is-active" : ""}`}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  {cat.name}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* --- ROW 3: LEVEL 3 (Children of L2) --- */}
      {activeL2 && activeL2.children.length > 0 && (
        <div className="mobile-category-strip__row mobile-category-strip__row--l3">
          <Swiper
            modules={[FreeMode]}
            freeMode
            slidesPerView="auto"
            spaceBetween={0}
          >
            <SwiperSlide className="mobile-category-strip__slide">
              <button
                className={`mobile-category-strip__item mobile-category-strip__item--sub ${selectedCategories.includes(activeL2.id) ? "is-active" : ""}`}
                onClick={() => handleCategoryClick(activeL2.id)}
              >
                {t("productsArchive.sidebar.allInParent", {
                  defaultValue: "All",
                })}
              </button>
            </SwiperSlide>
            {activeL2.children.map((cat) => (
              <SwiperSlide
                key={cat.id}
                className="mobile-category-strip__slide"
              >
                <button
                  className={`mobile-category-strip__item mobile-category-strip__item--sub ${selectedCategories.includes(cat.id) ? "is-active" : ""}`}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  {cat.name}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </div>
  );
};

export default MobileCategoryStrip;
